/*
 * File: FeedComponent.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 18, 2025 at 1:58 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 11, 2025 at 2:28 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Masonry from 'react-responsive-masonry';
import { useTranslation } from 'react-i18next';
import { useWindowResize } from 'beautiful-react-hooks';

// Utilities
import { buildDynamicMeta } from '../../../../components/Meta';
import { formatTextRedactions } from '../../../../../utilities/utilities';

// Services
import { fetchFeed } from '../../../../services/feed';

// Store
import {
	updateAllFeed,
	updateMessagesFeed,
	updateStoriesFeed,
	updateAllFeedActionSortBy,
	updateAllFeedActionFilterMedium,
	updateAllFeedSelectedTags,
	updateMessagesFeedActionSortBy,
	updateMessagesFeedActionFilterMessageType,
	updateStoriesFeedActionSortBy,
	updateStoriesFeedActionFilterMedium,
	updateStoriesFeedSelectedTags
} from '../../../../store/slices/feed/feed.slice';

// Components
import {
	FeedAction,
	FeedActionDropdown,
	InfiniteScroll,
	MessageCard,
	SchemaScript,
	StoryCard,
	SupportContent,
	ToolTip,
	Typography,
	Tag,
	ResponsiveMasonry
} from '../../../../components';

// Constants
import {
	AVAILABLE_I18N_NAMESPACES, INTERNAL_CONTENT_TYPES, MESSAGE_TYPES, SOURCES, STORY_MEDIA_TYPES
} from '../../../../../constants/base.constants';
import { MESSAGE_FEED_FILTER_MESSAGE_TYPE, STORY_FEED_FILTER_MEDIUM, STORY_FEED_SORT_BY } from '../../../../../constants/feed.constants';

// Styles
import * as S from './FeedComponent.styles';


/**
 * Constants
 */

const CONTENT_FILTER = {
	ALL: 'all',
	MESSAGES: 'messages',
	STORIES: 'stories'
};


/**
 * Component
 */

export const FeedComponent = ({
	className, meta, tagFamilies, activeContentFilter
}) => {

	// Get stories from hook
	const {
		feedAllStatus,
		feedAllItems,
		feedAllActionSortBy,
		feedAllActionFilterMedium,
		feedAllSelectedTags,
		feedMessagesStatus,
		feedMessagesItems,
		feedMessagesActionSortBy,
		feedMessagesActionFilterMessageType,
		feedStoriesStatus,
		feedStoriesItems,
		feedStoriesActionSortBy,
		feedStoriesActionFilterMedium,
		feedStoriesSelectedTags
	} = useSelector((state) => state.feed);

	// Create state
	const [showTagSelector, setShowTagSelector] = useState(false);
	const [tagSelectorHeight, setTagSelectorHeight] = useState(0);

	// Create all feed state
	const [currentPageAll, setCurrentPageAll] = useState(0);
	const [hasMoreToLoadAll, setMoreToLoadAll] = useState(feedAllItems.length === 0 || feedAllItems.length > 10);
	const [isFeedLoadingAll, setIsFeedLoadingAll] = useState(false);

	// Create messages feed state
	const [currentPageMessages, setCurrentPageMessages] = useState(0);
	const [hasMoreToLoadMessages, setMoreToLoadMessages] = useState(true);
	const [isFeedLoadingMessages, setIsFeedLoadingMessages] = useState(false);

	// Create stories feed state
	const [currentPageStories, setCurrentPageStories] = useState(0);
	const [hasMoreToLoadStories, setMoreToLoadStories] = useState(true);
	const [isFeedLoadingStories, setIsFeedLoadingStories] = useState(false);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get current story tags from hook
	const { status: tagStatus } = useSelector((state) => state.storyTag);

	// Create refs
	const displayedSupportIds = useRef([]);
	const firstUpdate = useRef(true);
	const tagSelectorRef = useRef();

	// Use hooks
	const dispatch = useDispatch();

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Create feed reference
	const feed = useMemo(() => {
		switch (activeContentFilter) {
			case CONTENT_FILTER.ALL:
				return feedAllItems;
			case CONTENT_FILTER.MESSAGES:
				return feedMessagesItems;
			case CONTENT_FILTER.STORIES:
				return feedStoriesItems;
			default:
				return [];
		}
	}, [activeContentFilter, feedAllItems, feedMessagesItems, feedStoriesItems]);

	// Create selected tags reference
	const selectedTags = useMemo(() => {
		switch (activeContentFilter) {
			case CONTENT_FILTER.ALL:
				return feedAllSelectedTags;
			case CONTENT_FILTER.STORIES:
				return feedStoriesSelectedTags;
			default:
				return [];
		}
	}, [activeContentFilter, feedAllSelectedTags, feedStoriesSelectedTags]);

	// Create current page
	const currentPage = useMemo(() => {
		switch (activeContentFilter) {
			case CONTENT_FILTER.ALL:
				return currentPageAll;
			case CONTENT_FILTER.MESSAGES:
				return currentPageMessages;
			case CONTENT_FILTER.STORIES:
				return currentPageStories;
			default:
				return 0;
		}
	}, [activeContentFilter, currentPageAll, currentPageMessages, currentPageStories]);

	// Create is feed loading
	const isFeedLoading = useMemo(() => {
		switch (activeContentFilter) {
			case CONTENT_FILTER.ALL:
				return isFeedLoadingAll;
			case CONTENT_FILTER.MESSAGES:
				return isFeedLoadingMessages;
			case CONTENT_FILTER.STORIES:
				return isFeedLoadingStories;
			default:
				return false;
		}
	}, [activeContentFilter, isFeedLoadingAll, isFeedLoadingMessages, isFeedLoadingStories]);

	// Create has more to load
	const hasMoreToLoad = useMemo(() => {
		switch (activeContentFilter) {
			case CONTENT_FILTER.ALL:
				return hasMoreToLoadAll;
			case CONTENT_FILTER.MESSAGES:
				return hasMoreToLoadMessages;
			case CONTENT_FILTER.STORIES:
				return hasMoreToLoadStories;
			default:
				return false;
		}
	}, [activeContentFilter, hasMoreToLoadAll, hasMoreToLoadMessages, hasMoreToLoadStories]);

	// Create feed action options
	const feedSortByOptions = useMemo(() => STORY_FEED_SORT_BY.map((option) => ({
		label: t(`feedActions.${option}`),
		value: option
	})), [t]);
	const feedFilterMediumOptions = useMemo(() => STORY_FEED_FILTER_MEDIUM.map((option) => ({
		label: t(`feedActions.${option}`),
		value: option
	})), [t]);
	const feedFilterMessageTypeOptions = useMemo(() => MESSAGE_FEED_FILTER_MESSAGE_TYPE.map((option) => ({
		label: t(`feedActions.${option}`),
		value: option
	})), [t]);

	// Create feed action messages type reference
	const feedMessagesActionFilterMessageTypeTitle = useMemo(() => {
		if (feedMessagesActionFilterMessageType.includes(MESSAGE_TYPES.HEALING_MESSAGE) && feedMessagesActionFilterMessageType.includes(MESSAGE_TYPES.HOPE_MESSAGE) && feedMessagesActionFilterMessageType.includes(MESSAGE_TYPES.SUPPORT_MESSAGE)) {
			return t('feedActions.messageTypeAllTitle');
		}
		return t('feedActions.messageTypeTitle');
	}, [feedMessagesActionFilterMessageType, t]);

	// Handle fetch feed
	const fetchFeedPreviews = async ({ page }) => {

		// Update state
		switch (activeContentFilter) {
			case CONTENT_FILTER.ALL:
				setIsFeedLoadingAll(true);
				break;
			case CONTENT_FILTER.MESSAGES:
				setIsFeedLoadingMessages(true);
				break;
			case CONTENT_FILTER.STORIES:
				setIsFeedLoadingStories(true);
				break;
			default:
				break;
		}

		// Create content types
		let contentTypes = [
			INTERNAL_CONTENT_TYPES.STORY,
			INTERNAL_CONTENT_TYPES.MESSAGE,
			INTERNAL_CONTENT_TYPES.MANTRA,
			INTERNAL_CONTENT_TYPES.SUPPORT_IMAGE,
			INTERNAL_CONTENT_TYPES.SUPPORT_VIDEO
		];
		if (activeContentFilter === CONTENT_FILTER.MESSAGES) {
			contentTypes = [
				INTERNAL_CONTENT_TYPES.MESSAGE,
				INTERNAL_CONTENT_TYPES.MANTRA,
				INTERNAL_CONTENT_TYPES.SUPPORT_IMAGE,
				INTERNAL_CONTENT_TYPES.SUPPORT_VIDEO
			];
		} else if (activeContentFilter === CONTENT_FILTER.STORIES) {
			contentTypes = [
				INTERNAL_CONTENT_TYPES.STORY,
				INTERNAL_CONTENT_TYPES.MANTRA,
				INTERNAL_CONTENT_TYPES.SUPPORT_IMAGE,
				INTERNAL_CONTENT_TYPES.SUPPORT_VIDEO
			];
		}

		// Create medium types
		let mediumTypes = [STORY_MEDIA_TYPES.NARRATIVE, STORY_MEDIA_TYPES.ARTWORK];
		if (activeContentFilter === CONTENT_FILTER.ALL) {
			mediumTypes = [...feedAllActionFilterMedium];
		} else if (activeContentFilter === CONTENT_FILTER.STORIES) {
			mediumTypes = [...feedStoriesActionFilterMedium];
		}

		// Create sort by
		let sortBy = null;
		if (activeContentFilter === CONTENT_FILTER.ALL) {
			sortBy = feedAllActionSortBy;
		} else if (activeContentFilter === CONTENT_FILTER.MESSAGES) {
			sortBy = feedMessagesActionSortBy;
		} else if (activeContentFilter === CONTENT_FILTER.STORIES) {
			sortBy = feedStoriesActionSortBy;
		}

		// Create tags
		let tags = null;
		if (activeContentFilter === CONTENT_FILTER.ALL) {
			tags = feedAllSelectedTags;
		} else if (activeContentFilter === CONTENT_FILTER.STORIES) {
			tags = feedStoriesSelectedTags;
		}

		// Fetch feed previews
		const { data: { result: { feed: previews, support_ids: supportIds } } } = await fetchFeed({
			page,
			sortBy,
			contentTypes,
			mediumTypes,
			messageTypes: feedMessagesActionFilterMessageType,
			displayedSupportIds: displayedSupportIds.current,
			...tags ? {
				tags
			} : undefined
		});

		// Deduplicate support ids
		displayedSupportIds.current = [...new Set(supportIds)];

		// Update feed
		switch (activeContentFilter) {
			case CONTENT_FILTER.ALL:
				dispatch(updateAllFeed(page === 0 ? previews : [...feed, ...previews]));
				break;
			case CONTENT_FILTER.MESSAGES:
				dispatch(updateMessagesFeed(page === 0 ? previews : [...feed, ...previews]));
				break;
			case CONTENT_FILTER.STORIES:
				dispatch(updateStoriesFeed(page === 0 ? previews : [...feed, ...previews]));
				break;
			default:
				break;
		}

		// Update state
		switch (activeContentFilter) {
			case CONTENT_FILTER.ALL:

				// Update page state
				setCurrentPageAll(page);

				// Update more to load state
				setMoreToLoadAll(previews.length > 10);

				// Update state
				setIsFeedLoadingAll(false);

				// Break
				break;
			case CONTENT_FILTER.MESSAGES:

				// Update page state
				setCurrentPageMessages(page);

				// Update more to load state
				setMoreToLoadMessages(previews.length > 10);

				// Update state
				setIsFeedLoadingMessages(false);

				// Break
				break;
			case CONTENT_FILTER.STORIES:

				// Update page state
				setCurrentPageStories(page);

				// Update more to load state
				setMoreToLoadStories(previews.length > 10);

				// Update state
				setIsFeedLoadingStories(false);

				// Break
				break;
			default:
				break;
		}
	};

	// Handle select tag
	const selectTag = (tag) => {
		if (activeContentFilter === CONTENT_FILTER.ALL) {
			if (selectedTags.includes(tag.tag_id)) {
				dispatch(updateAllFeedSelectedTags(selectedTags.filter((tId) => tId !== tag.tag_id)));
			} else {
				dispatch(updateAllFeedSelectedTags([...selectedTags, tag.tag_id]));
			}
		} else if (activeContentFilter === CONTENT_FILTER.STORIES) {
			if (selectedTags.includes(tag.tag_id)) {
				dispatch(updateStoriesFeedSelectedTags(selectedTags.filter((tId) => tId !== tag.tag_id)));
			} else {
				dispatch(updateStoriesFeedSelectedTags([...selectedTags, tag.tag_id]));
			}
		}
	};

	// Handle actions on filter change
	useEffect(() => {
		if (!firstUpdate.current) {

			// Fetch feed if necessary
			switch (activeContentFilter) {
				case CONTENT_FILTER.ALL: {
					if (feedAllStatus === 'final') {

						// Fetch feed
						fetchFeedPreviews({ page: 0 });
					}
					break;
				}
				case CONTENT_FILTER.MESSAGES: {
					if (feedMessagesStatus === 'final') {

						// Fetch feed
						fetchFeedPreviews({ page: 0 });
					}
					break;
				}
				case CONTENT_FILTER.STORIES: {
					if (feedStoriesStatus === 'final') {

						// Fetch feed
						fetchFeedPreviews({ page: 0 });
					}
					break;
				}
				default:
					break;
			}
		}

		// Update first update
		firstUpdate.current = false;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		feedAllActionSortBy,
		feedAllActionFilterMedium,
		feedAllSelectedTags,
		feedMessagesActionSortBy,
		feedMessagesActionFilterMessageType,
		feedStoriesActionSortBy,
		feedStoriesActionFilterMedium,
		feedStoriesSelectedTags,
	]);

	// Handle actions on feed all status changed
	useEffect(() => {
		if (activeContentFilter === CONTENT_FILTER.ALL && feedAllStatus !== 'final') {

			// Fetch feed
			fetchFeedPreviews({ page: currentPage });
		}
	}, [activeContentFilter, feedAllStatus]);

	// Handle actions on feed messages status changed
	useEffect(() => {
		if (activeContentFilter === CONTENT_FILTER.MESSAGES && feedMessagesStatus !== 'final') {

			// Fetch feed
			fetchFeedPreviews({ page: currentPage });
		}
	}, [activeContentFilter, feedMessagesStatus]);

	// Handle actions on feed stories status changed
	useEffect(() => {
		if (activeContentFilter === CONTENT_FILTER.STORIES && feedStoriesStatus !== 'final') {

			// Fetch feed
			fetchFeedPreviews({ page: currentPage });
		}
	}, [activeContentFilter, feedStoriesStatus]);

	// Handle actions on component load and active content filter change
	useEffect(() => {

		// Hide tag selector
		setShowTagSelector(false);

	}, [activeContentFilter]);

	// Handle actions on tag status update
	useEffect(() => {
		if (tagStatus === 'final') {

			// Set widget height
			setTagSelectorHeight(tagSelectorRef.current.clientHeight);
		}
	}, [tagStatus]);

	// Handle actions on window resize
	useWindowResize(() => {

		// Set widget height
		setTagSelectorHeight(tagSelectorRef.current.clientHeight);
	});

	// Render component
	return (
		<S.Wrapper className={className}>

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'https://schema.org',
				'@type': 'CollectionPage',
				'@id': `${partnerDomain}/feed/#BreadcrumbList`,
				name: buildDynamicMeta(meta.title, { partner }),
				description: buildDynamicMeta(meta.description, { partner }),
				url: `${partnerDomain}${locale.localePath}`,
				inLanguage: locale.localeBaseId,
				isPartOf: {
					'@id': `${partnerDomain}/#WebSite`
				},
				publisher: {
					'@id': `${partner.website}/#Organization`,
				},
				breadcrumb: {
					'@id': `${partnerDomain}/feed/#BreadcrumbList`
				},
				mainEntity: feed?.filter((item) => item.internal_content_type === INTERNAL_CONTENT_TYPES.STORY || item.internal_content_type === INTERNAL_CONTENT_TYPES.MESSAGE).map((item) => (item.internal_content_type === INTERNAL_CONTENT_TYPES.STORY ? {
					'@type': ['CreativeWork', 'ShortStory'],
					'@id': `${partnerDomain}/story/${item.link_component}/#CreativeWork`,
					name: formatTextRedactions(!item.title ? `#${item.number}` : item.title),
					text: formatTextRedactions(item.content),
					url: `${partnerDomain}${locale.localePath}/story/${item.link_component}`,
					isAccessibleForFree: true
				} : {
					'@type': ['CreativeWork', 'Message'],
					'@id': `${partnerDomain}/message/${item.slug}/#CreativeWork`,
					name: formatTextRedactions(`#${item.number}`),
					text: formatTextRedactions(item.content),
					url: `${partnerDomain}${locale.localePath}/message/${item.slug}`,
					isAccessibleForFree: true
				}))
			}}
			/>

			{/* Feed Action Bar */}
			<S.FeedActionBar>

				{/* Story Specific Actions */}
				{activeContentFilter === CONTENT_FILTER.ALL && (
					<>
						{/* Feed Sort Action */}
						<FeedActionDropdown
							title={t('feedActions.sortByTitle')}
							selected={feedAllActionSortBy}
							selectedTitle={t(`feedActions.${feedAllActionSortBy}`)}
							options={feedSortByOptions}
							multipleSelection={false}
							widgetWidth={150}
							onSelect={({ value }) => { dispatch(updateAllFeedActionSortBy(value)); }}
						/>

						{/* Story Medium Action */}
						<FeedActionDropdown
							title={t('feedActions.mediumFilterTitle')}
							selected={feedAllActionFilterMedium}
							selectedTitle={t('feedActions.mediumFilterTitle')}
							options={feedFilterMediumOptions}
							multipleSelection
							widgetWidth={150}
							onSelect={({ value }) => {
								if (feedAllActionFilterMedium.includes(value)) {
									if (feedAllActionFilterMedium.length > 1) {
										dispatch(updateAllFeedActionFilterMedium(feedAllActionFilterMedium.filter((o) => o !== value)));
									}
								} else {
									dispatch(updateAllFeedActionFilterMedium([...feedAllActionFilterMedium, value]));
								}
							}}
						/>

						{/* Story Tags Action */}
						<FeedAction
							title={t('feedActions.filterByTagTitle')}
							isOpen={showTagSelector}
							onClick={() => { setShowTagSelector(!showTagSelector); }}
							titleDecoration={selectedTags.length > 0 ? (
								<S.ActiveTagCount>
									<Typography tag="p" variation="3" weight="medium">{selectedTags.length}</Typography>
								</S.ActiveTagCount>
							) : null}
						/>
					</>
				)}

				{/* Story Specific Actions */}
				{activeContentFilter === CONTENT_FILTER.MESSAGES && (
					<>
						{/* Feed Sort Action */}
						<FeedActionDropdown
							title={t('feedActions.sortByTitle')}
							selected={feedMessagesActionSortBy}
							selectedTitle={t(`feedActions.${feedMessagesActionSortBy}`)}
							options={feedSortByOptions}
							multipleSelection={false}
							widgetWidth={150}
							onSelect={({ value }) => { dispatch(updateMessagesFeedActionSortBy(value)); }}
						/>

						{/* Message Type Filter Action */}
						<FeedActionDropdown
							title={t('feedActions.messageTypeTitle')}
							selected={feedMessagesActionFilterMessageType}
							selectedTitle={feedMessagesActionFilterMessageTypeTitle}
							options={feedFilterMessageTypeOptions}
							multipleSelection
							widgetWidth={210}
							onSelect={({ value }) => {
								if (feedMessagesActionFilterMessageType.includes(value)) {
									if (feedMessagesActionFilterMessageType.length > 1) {
										dispatch(updateMessagesFeedActionFilterMessageType(feedMessagesActionFilterMessageType.filter((o) => o !== value)));
									}
								} else {
									dispatch(updateMessagesFeedActionFilterMessageType([...feedMessagesActionFilterMessageType, value]));
								}
							}}
						/>
					</>
				)}

				{/* Story Specific Actions */}
				{activeContentFilter === CONTENT_FILTER.STORIES && (
					<>
						{/* Feed Sort Action */}
						<FeedActionDropdown
							title={t('feedActions.sortByTitle')}
							selected={feedStoriesActionSortBy}
							selectedTitle={t(`feedActions.${feedStoriesActionSortBy}`)}
							options={feedSortByOptions}
							multipleSelection={false}
							widgetWidth={150}
							onSelect={({ value }) => { dispatch(updateStoriesFeedActionSortBy(value)); }}
						/>

						{/* Story Medium Action */}
						<FeedActionDropdown
							title={t('feedActions.mediumFilterTitle')}
							selected={feedStoriesActionFilterMedium}
							selectedTitle={t('feedActions.mediumFilterTitle')}
							options={feedFilterMediumOptions}
							multipleSelection
							widgetWidth={150}
							onSelect={({ value }) => {
								if (feedStoriesActionFilterMedium.includes(value)) {
									if (feedStoriesActionFilterMedium.length > 1) {
										dispatch(updateStoriesFeedActionFilterMedium(feedStoriesActionFilterMedium.filter((o) => o !== value)));
									}
								} else {
									dispatch(updateStoriesFeedActionFilterMedium([...feedStoriesActionFilterMedium, value]));
								}
							}}
						/>

						{/* Story Tags Action */}
						<FeedAction
							title={t('feedActions.filterByTagTitle')}
							isOpen={showTagSelector}
							onClick={() => { setShowTagSelector(!showTagSelector); }}
							titleDecoration={selectedTags.length > 0 ? (
								<S.ActiveTagCount>
									<Typography tag="p" variation="3" weight="medium">{selectedTags.length}</Typography>
								</S.ActiveTagCount>
							) : null}
						/>
					</>
				)}

			</S.FeedActionBar>

			{/* Tag Selector Widget */}
			<S.TagSelectorWidget $isOpen={showTagSelector} $height={tagSelectorHeight} className="animate">
				<S.TagFamilyContainer ref={tagSelectorRef}>
					{tagFamilies.map((family) => (
						<S.TagFamily key={family.family}>

							{/* Title */}
							<Typography tag="p" weight="medium" variation="2">{family.family_name}</Typography>

							{/* Tags */}
							<S.TagContainer>
								{family.tags.map((tag) => (
									<Tag
										key={tag.tag_id}
										size="compact"
										onClick={() => { selectTag(tag); }}
										isActive={selectedTags.some((tt) => tt === tag.tag_id)}
										data-tip={tag.description_locales[locale.localeBaseId]}
										data-for={tag.tag_id}
									>
										{tag.description_locales[locale.localeBaseId] && <ToolTip id={tag.tag_id} />}
										<Typography tag="p" variation="3" weight="medium" center={false}>
											{tag.title_locales[locale.localeBaseId]}
										</Typography>
									</Tag>
								))}
							</S.TagContainer>

						</S.TagFamily>
					))}
				</S.TagFamilyContainer>
			</S.TagSelectorWidget>

			{/* Infinite Scroll */}
			<InfiniteScroll
				dataLength={feed.length}
				next={() => { if (!isFeedLoading) fetchFeedPreviews({ page: currentPage + 1 }); }}
				refresh={async () => { await fetchFeedPreviews({ page: 0 }); return true; }}
				loader={(
					<ResponsiveMasonry style={{ marginTop: feed.length > 0 ? '22px' : '0px' }}>
						<Masonry gutter="22px">
							<StoryCard isPlaceholder index={0} />
							<StoryCard isPlaceholder index={1} />
							<StoryCard isPlaceholder index={2} />
							<StoryCard isPlaceholder index={3} />
							<SupportContent isPlaceholder />
							<StoryCard isPlaceholder index={4} />
							<StoryCard isPlaceholder index={5} />
							<StoryCard isPlaceholder index={6} />
							<StoryCard isPlaceholder index={7} />
						</Masonry>
					</ResponsiveMasonry>
				)}
				hasMore={hasMoreToLoad}
			>

				{/* Content Masonry */}
				<ResponsiveMasonry>
					<Masonry gutter="22px">
						{feed.map((item, index) => {
							switch (item.internal_content_type) {
								case INTERNAL_CONTENT_TYPES.STORY:
									return (
										<StoryCard
											key={item.story_id}
											story={item}
											source={SOURCES.STORIES}
											context={activeContentFilter}
											index={index}
										/>
									);
								case INTERNAL_CONTENT_TYPES.MESSAGE:
									return (
										<MessageCard
											key={item.message_id}
											message={item}
											source={SOURCES.STORIES}
											context={activeContentFilter}
											index={index}
										/>
									);
								case INTERNAL_CONTENT_TYPES.MANTRA:
									return (
										<SupportContent
											key={item.id}
											item={item}
											index={index}
										/>
									);
								case INTERNAL_CONTENT_TYPES.SUPPORT_IMAGE:
									return (
										<SupportContent
											key={item.id}
											item={item}
											index={index}
										/>
									);
								case INTERNAL_CONTENT_TYPES.SUPPORT_VIDEO:
									return (
										<SupportContent
											key={item.id}
											item={item}
											index={index}
										/>
									);
								default:
									return null;
							}
						})}
					</Masonry>
				</ResponsiveMasonry>

			</InfiniteScroll>
		</S.Wrapper>
	);
};


/**
 * Configuration
 */

FeedComponent.displayName = 'FeedComponent';
FeedComponent.propTypes = {
	className: PropTypes.string,
	meta: PropTypes.shape(),
	tagFamilies: PropTypes.arrayOf(PropTypes.shape()),
	activeContentFilter: PropTypes.string
};
FeedComponent.defaultProps = {
	className: null,
	meta: {},
	tagFamilies: [],
	activeContentFilter: CONTENT_FILTER.ALL
};
